import {announce} from '@github-ui/aria-live'
import {ApplySuggestionDialog} from '@github-ui/diff-lines/apply-suggestion-dialog'
import {useQueryClient} from '@github-ui/react-query'
import {useAnalytics} from '@github-ui/use-analytics'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {Button} from '@primer/react'
import {type FC, type ReactNode, type RefObject, useCallback, useRef, useState} from 'react'
import {FINDINGS_ANALYTICS_EVENTS} from './analytics'
import {getFindingApplicationKey, getFindingTitle} from './entity-helpers'
import type {Finding} from './types'
import {findingsQueryKey} from './use-findings'

const DEFAULT_COMMIT_MESSAGE = 'Apply code review finding suggestion from Copilot'
const GENERIC_ERROR_MESSAGE = 'Failed to apply this suggestion. Please try again.'
const SELECTION_CHANGED_MESSAGE =
  'The pull request or suggestion changed. Close and reopen this dialog to review the latest suggestion.'

interface ApplyFindingSuggestionProps {
  finding: Finding
  headSha?: string
  pullRequestBasePath: string
  applied: boolean
  onApplyStarted?: () => void
  onApplyFailed?: () => void
  onApplied: () => void
  fallbackReturnFocusRef?: RefObject<HTMLElement | null>
}

interface ApplySuggestionResponse {
  commit_oid?: unknown
  finding_ids?: unknown
}

interface ApplySuggestionSelection {
  finding: Finding
  headSha: string
}

interface ApplySuggestionErrorResponse {
  error?: unknown
  code?: unknown
}

const getApplyErrorMessage = (status: number, data: ApplySuggestionErrorResponse | null): string => {
  if (data?.code === 'HEAD_OID_MISMATCH') {
    return 'The pull request changed. Refresh and try again.'
  }
  if (data?.code === 'SUGGESTION_CONFLICT' || status === 409) {
    return 'This suggestion conflicts with changes on the branch. Refresh and try again.'
  }
  if (typeof data?.error === 'string' && data.error.trim()) {
    return data.error
  }
  return GENERIC_ERROR_MESSAGE
}

const parseResponseJson = async <T,>(response: Response): Promise<T | null> => {
  try {
    return (await response.json()) as T
  } catch {
    return null
  }
}

const reportApplyError = (error: Error, onError: (error: Error, type?: string, friendlyMessage?: string) => void) => {
  onError(error, 'applyFindingSuggestion', error.message)
  announce(error.message)
}

export const useApplyFindingSuggestion = ({
  finding,
  headSha,
  pullRequestBasePath,
  applied,
  onApplyStarted,
  onApplyFailed,
  onApplied,
  fallbackReturnFocusRef,
}: ApplyFindingSuggestionProps): {action: ReactNode; dialog: ReactNode} => {
  const [selection, setSelection] = useState<ApplySuggestionSelection | null>(null)
  const applyButtonRef = useRef<HTMLButtonElement>(null)
  const dialogReturnFocusRef = useRef<HTMLElement>(null)
  const queryClient = useQueryClient()
  const {sendAnalyticsEvent} = useAnalytics()
  const setApplyButtonRef = useCallback(
    (button: HTMLButtonElement | null) => {
      if (!button) return

      applyButtonRef.current = button
      return () => {
        if (document.activeElement === button) {
          fallbackReturnFocusRef?.current?.focus({preventScroll: true})
        }
        if (dialogReturnFocusRef.current === button) {
          dialogReturnFocusRef.current = fallbackReturnFocusRef?.current ?? null
        }
        if (applyButtonRef.current === button) {
          applyButtonRef.current = null
        }
      }
    },
    [fallbackReturnFocusRef],
  )

  const handleCommit = useCallback(
    async (
      commitMessage: string,
      onError: (error: Error, type?: string, friendlyMessage?: string) => void,
      onCompleted: () => void,
    ) => {
      if (
        !selection ||
        !headSha ||
        getFindingApplicationKey(selection.finding, selection.headSha) !== getFindingApplicationKey(finding, headSha)
      ) {
        reportApplyError(new Error(SELECTION_CHANGED_MESSAGE), onError)
        return
      }

      sendAnalyticsEvent(
        FINDINGS_ANALYTICS_EVENTS.applySuggestion.type,
        FINDINGS_ANALYTICS_EVENTS.applySuggestion.target,
        {finding_id: selection.finding.id},
      )
      onApplyStarted?.()

      try {
        const response = await reactFetchJSON(`${pullRequestBasePath}/code_review_findings/apply_suggestions`, {
          method: 'POST',
          body: {
            findings: [{id: selection.finding.id, partitionRef: selection.finding.partitionRef}],
            expected_head_sha: selection.headSha,
            commit_message: commitMessage,
          },
        })
        const data = await parseResponseJson<ApplySuggestionResponse & ApplySuggestionErrorResponse>(response)

        if (!response.ok) {
          if (response.status === 409 || data?.code === 'HEAD_OID_MISMATCH' || data?.code === 'SUGGESTION_CONFLICT') {
            await queryClient.invalidateQueries({queryKey: findingsQueryKey(pullRequestBasePath)})
          }
          throw new Error(getApplyErrorMessage(response.status, data))
        }
        if (
          typeof data?.commit_oid !== 'string' ||
          !Array.isArray(data.finding_ids) ||
          !data.finding_ids.includes(selection.finding.id)
        ) {
          throw new Error(GENERIC_ERROR_MESSAGE)
        }

        dialogReturnFocusRef.current = fallbackReturnFocusRef?.current ?? null
        setSelection(null)
        onCompleted()
        requestAnimationFrame(() => {
          announce('Suggestion applied')
          onApplied()
          void queryClient.invalidateQueries({queryKey: findingsQueryKey(pullRequestBasePath)})
          requestAnimationFrame(() => {
            if (fallbackReturnFocusRef?.current?.isConnected || document.activeElement !== document.body) return
            document.getElementById('findings-content')?.focus({preventScroll: true})
          })
        })
      } catch (error: unknown) {
        onApplyFailed?.()
        const applyError = error instanceof Error ? error : new Error(GENERIC_ERROR_MESSAGE)
        reportApplyError(applyError, onError)
      }
    },
    [
      fallbackReturnFocusRef,
      finding,
      headSha,
      onApplyFailed,
      onApplyStarted,
      onApplied,
      pullRequestBasePath,
      queryClient,
      selection,
      sendAnalyticsEvent,
    ],
  )

  const action =
    headSha || applied || selection ? (
      <Button
        ref={setApplyButtonRef}
        size="small"
        disabled={applied || !headSha}
        aria-label={`Apply suggestion: ${getFindingTitle(finding)}`}
        onClick={() => {
          if (headSha) {
            dialogReturnFocusRef.current = applyButtonRef.current
            setSelection({finding, headSha})
          }
        }}
      >
        Apply suggestion
      </Button>
    ) : null

  return {
    action,
    dialog: selection ? (
      <ApplySuggestionDialog
        authorLogins={['copilot']}
        batchSize={1}
        commentKind="ccr"
        defaultCommitMessage={DEFAULT_COMMIT_MESSAGE}
        dialogTitle="Apply suggestion"
        submitButtonLabel="Apply Suggestion"
        onClose={() => setSelection(null)}
        onCommit={handleCommit}
        returnFocusRef={dialogReturnFocusRef}
      />
    ) : null,
  }
}

export const ApplyFindingSuggestion: FC<ApplyFindingSuggestionProps> = props => {
  const {action, dialog} = useApplyFindingSuggestion(props)

  return (
    <>
      {action}
      {dialog}
    </>
  )
}
